import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { artists } from '../data/artists';
import styles from '../ArtistSpotlight.module.scss';

const platformNames = {
    instagram: "Instagram",
    twitter: "Twitter",
    bandcamp: "Bandcamp",
    soundcloud: "SoundCloud",
    spotify: "Spotify",
    apple: "Apple Music",
    tiktok: "TikTok",
    youtube: "YouTube"
    // Add more mappings as needed
};

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const DEFAULT_SOUNDCLOUD_TRACK_LIMIT = 3;

function buildSoundCloudEmbedUrl(trackOrProfileUrl) {
    const encodedUrl = encodeURIComponent(trackOrProfileUrl);
    return `https://w.soundcloud.com/player/?url=${encodedUrl}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true`;
}

const ArtistDetail = () => {
    const { artistId } = useParams();
    const artist = artists.find(a => a.id === artistId);

    if (!artist) {
        return <div className={styles.notFound}>Artist not found</div>;
    }

    return (
        <div className={styles.detailContainer}>
            <Link to="/artistspotlight" className={styles.backLink}>← Back to Artists</Link>
            <ArtistDetailInfo artist={artist} />
        </div>
    );
};

export default ArtistDetail;

export const ArtistDetailInfo = ({ artist }) => {
    const hasYouTubeLinks = Array.isArray(artist.youtubeLinks) && artist.youtubeLinks.length > 0;
    const hasSoundCloudProfile = Boolean(artist.socials?.soundcloud);
    const staticSoundCloudTrackUrls = useMemo(() => (
        Array.isArray(artist.soundcloudTrackUrls)
            ? artist.soundcloudTrackUrls.filter(Boolean)
            : []
    ), [artist.soundcloudTrackUrls]);
    const [soundCloudTrackUrls, setSoundCloudTrackUrls] = useState(staticSoundCloudTrackUrls);
    const [isLoadingSoundCloudTracks, setIsLoadingSoundCloudTracks] = useState(false);

    useEffect(() => {
        const profileUrl = artist.socials?.soundcloud;

        if (hasYouTubeLinks || !profileUrl) {
            setSoundCloudTrackUrls(staticSoundCloudTrackUrls);
            setIsLoadingSoundCloudTracks(false);
            return;
        }

        if (staticSoundCloudTrackUrls.length > 0) {
            setSoundCloudTrackUrls(staticSoundCloudTrackUrls);
            setIsLoadingSoundCloudTracks(false);
            return;
        }

        const limit = Number.isInteger(artist.soundcloudTopTracks) && artist.soundcloudTopTracks > 0
            ? artist.soundcloudTopTracks
            : DEFAULT_SOUNDCLOUD_TRACK_LIMIT;
        const controller = new AbortController();
        let isMounted = true;

        const fetchTopTracks = async () => {
            setIsLoadingSoundCloudTracks(true);
            setSoundCloudTrackUrls([]);

            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/soundcloud/top-tracks?profileUrl=${encodeURIComponent(profileUrl)}&limit=${limit}`,
                    { signal: controller.signal }
                );

                const contentType = response.headers.get('content-type') || '';
                if (!contentType.includes('application/json')) {
                    throw new Error('Unable to load SoundCloud tracks.');
                }

                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.error || 'Unable to load SoundCloud tracks.');
                }

                const payload = await response.json();
                const topTrackUrls = Array.isArray(payload?.tracks)
                    ? payload.tracks.map((track) => track?.permalinkUrl).filter(Boolean)
                    : [];

                if (!isMounted) return;

                setSoundCloudTrackUrls(topTrackUrls);
                if (topTrackUrls.length === 0) {
                }
            } catch (error) {
                if (!isMounted || error.name === 'AbortError') return;
                setSoundCloudTrackUrls([]);
            } finally {
                if (isMounted) {
                    setIsLoadingSoundCloudTracks(false);
                }
            }
        };

        fetchTopTracks();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [
        artist.id,
        artist.socials?.soundcloud,
        artist.soundcloudTopTracks,
        hasYouTubeLinks,
        staticSoundCloudTrackUrls
    ]);

    const hasSoundCloudTracks = soundCloudTrackUrls.length > 0;
    const shouldShowSoundCloudFallback = hasSoundCloudProfile && !hasYouTubeLinks && !hasSoundCloudTracks && !isLoadingSoundCloudTracks;

    return (
        <div className={styles.artistDetail}>
            <h1 className={styles.detailName}>{artist.name}</h1>
            <p className={styles.detailBio}>{artist.bio}</p>
            <div className={styles.detailSocials}>
                {Object.entries(artist.socials || {}).map(([platform, url]) => {
                    if (!url) return null; // skip if no URL
                    const displayName = platformNames[platform] || platform;
                    return (
                        <a key={platform} href={url} target="_blank" rel="noopener noreferrer">
                            {displayName}
                        </a>
                    );
                })}
            </div>
            {hasYouTubeLinks && (
                <>
                    <h2 className={styles.videoSectionTitle}>Featured Sets / Songs</h2>
                    <div className={styles.videoContainer}>
                        {artist.youtubeLinks.map((link, index) => (
                            <div key={index} className={styles.videoWrapper}>
                                <iframe
                                    width="560"
                                    height="315"
                                    src={link}
                                    title={`YouTube video player ${index}`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {!hasYouTubeLinks && hasSoundCloudProfile && (
                <>

                    {isLoadingSoundCloudTracks && (
                        <p className={styles.mediaNotice}>Loading popular SoundCloud tracks...</p>
                    )}

                    {hasSoundCloudTracks && (
                        <div className={styles.soundcloudContainer}>
                            {soundCloudTrackUrls.map((trackUrl, index) => (
                                <div key={`${trackUrl}-${index}`} className={styles.soundcloudWrapper}>
                                    <iframe
                                        title={`${artist.name} SoundCloud track ${index + 1}`}
                                        width="100%"
                                        height="166"
                                        scrolling="no"
                                        frameBorder="no"
                                        allow="autoplay"
                                        src={buildSoundCloudEmbedUrl(trackUrl)}
                                    ></iframe>
                                </div>
                            ))}
                        </div>
                    )}
                    {shouldShowSoundCloudFallback && (
                        <div className={styles.soundcloudContainer}>
                            <div className={styles.soundcloudWrapper}>
                                <iframe
                                    title={`${artist.name} SoundCloud profile`}
                                    width="100%"
                                    height="166"
                                    scrolling="no"
                                    frameBorder="no"
                                    allow="autoplay"
                                    src={buildSoundCloudEmbedUrl(artist.socials.soundcloud)}
                                ></iframe>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
